import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Tooltip, Typography } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';

import { ActionButton } from 'components/atoms';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    width: '100%',
    fontSize: 18,
  },
  wrapper: {
    marginTop: 20,
    backgroundColor: 'transparent',
  },
  title: {
    display: 'flex',
    color: '#b8b8b8',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    width: '100%',
    '& h3': {
      marginBottom: 20,
      borderBottom: 'solid 4px #981248',
      marginRight: 40,
    },
    [theme.breakpoints.up('md')]: {
      position: 'relative',
    },
  },
  content: {
    backgroundColor: 'transparent',
    width: '100%',
    height: 'calc(100% - 102px);',
    whiteSpace: 'pre-wrap',
    fontFamily: 'Roboto',
    color: '#000000',
    '& > ul': {
      marginLeft: 20,
    },
  },
  actionContainer: {
    marginBottom: 20,
    '& > .MuiButton-root': {
      paddingRight: 16,
      paddingLeft: 16,
      background: '#981B49',
    },
    '& .MuiButton-outlined': {
      background: 'white',
      color: '#981B49',
      backgroundColor: 'transparent',
      border: '2px solid #981B49',
    },
  },
  imageMap: {
    mixBlendMode: 'multiply',
    height: 200,
    position: 'absolute',
    right: 0,
    [theme.breakpoints.down('sm')]: {
      position: 'inherit',
      left: 50,
      height: 97,
    },
  },
  textLink: {
    color: '#000000 !important',
    '&:link': {
      color: '#000000',
    },
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:visited': {
      color: '#000000',
    },
  },
  email: {
    color: '#4284ea',
    textDecoration: 'underline',
    '&:link': {
      color: '#4284ea',
    },
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:visited': {
      color: '#4284ea',
    },
    '&:active': {
      color: '#4284ea',
    },
  },
  summary: {
    marginBottom: theme.spacing(3),
    '& h4': {
      marginBottom: theme.spacing(1),
    },
    '& > ul': {
      listStyleType: 'none',
    },
    '& .value': {
      marginLeft: theme.spacing(0.5),
    },
  },
  policies: {
    marginBottom: theme.spacing(3),
    '& h4': {
      marginBottom: theme.spacing(1),
    },
    '& > ul': {
      listStyleType: 'none',
    },
    '& a': {
      color: '#981248',
    },
  },
  characteristics: {
    marginBottom: 20,
    '& > table': {
      width: '100%',
    },
    '& ~ .MuiCollapse-container': {
      marginBottom: 20,
    },
  },
  characteristicsUnits: {
    maxWidth: 200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 100,
    },
  },
  characteristicsUnitsTd: {
    maxWidth: 250,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 110,
    },
  },
  description: {
    marginBottom: 20,
  },
}));

const MapsPreview = props => {
  const { className, data } = props; // eslint-disable-line no-unused-vars
  const classes = useStyles();
  const { t } = useTranslation();
  const [isProfileView, setIsProfileView] = useState(false);
  const [isShowMore, setIsShowMore] = useState(false);

  const { policyDocuments = [], keyDocuments = [] } = data;

  function urlDomain(url) {
    if (!url) return '';

    var a = document.createElement('a');
    a.href = url;

    return a.hostname;
  }

  useEffect(() => {
    setIsProfileView(false);
  }, [data]);

  const handleToggleView = () => {
    setIsProfileView(prev => !prev);
  };

  const handleToggleShowMore = () => {
    setIsShowMore(prev => !prev);
  };

  const unitsText = `${data.units?.split(',').length} (${data.units}`;

  return (
    <div className={clsx(classes.root)}>
      <div className={clsx(classes.wrapper)}>
        <div className={clsx(classes.title)}>
          <div>
            <Typography variant="h3">
              {t('MUNICIPALITY.TITLE', data)}
            </Typography>
          </div>
          {data.mapImageUrl && (
            <img
              className={classes.imageMap}
              alt={data.name}
              src={data.mapImageUrl}
            />
          )}
        </div>
        {!isProfileView && (
          <div className={clsx(classes.content)} data-aos="fade-right">
            <div className={classes.summary}>
              <ul>
                <li>
                  {t('MUNICIPALITY.WEB_PAGE')}
                  <a
                    className={clsx('value', classes.textLink)}
                    target="_blank"
                    rel="noreferrer"
                    alt={data.name}
                    href={data.url}
                  >
                    {urlDomain(data.url)}
                  </a>
                </li>
                <li>
                  {t('MUNICIPALITY.MAYOR')} {data.mayor}
                </li>
                <li>
                  {t('MUNICIPALITY.MAYOR_EMAIL')}
                  <a
                    className={clsx('value', classes.email)}
                    target="_blank"
                    rel="noreferrer"
                    alt={data.mayorEmail}
                    href={`mailto:${data.mayorEmail}`}
                  >
                    {data.mayorEmail}
                  </a>
                </li>
                <li>
                  {t('MUNICIPALITY.SMG_COORDINATOR')} {data.smgCoordinator}
                </li>
                <li>
                  {t('MUNICIPALITY.MAYOR_EMAIL')}
                  <a
                    className={clsx('value', classes.email)}
                    target="_blank"
                    rel="noreferrer"
                    alt={data.smgCoordinatorEmail}
                    href={`mailto:${data.smgCoordinatorEmail}`}
                  >
                    {data.smgCoordinatorEmail}
                  </a>
                </li>
                <li>
                  {t('MUNICIPALITY.EXECUTING_OFFICER')} {data.executingOfficer}
                </li>
                <li>
                  {t('MUNICIPALITY.MAYOR_EMAIL')}
                  <a
                    className={clsx('value', classes.email)}
                    target="_blank"
                    rel="noreferrer"
                    alt={data.executingOfficerEmail}
                    href={`mailto:${data.executingOfficerEmail}`}
                  >
                    {data.executingOfficerEmail}
                  </a>
                </li>
              </ul>
            </div>
            <div className={classes.actionContainer}>
              <ActionButton
                variant="contained"
                color="primary"
                size="small"
                className={clsx(classes.loginButton)}
                onClick={e => handleToggleView()}
              >
                {t('MUNICIPALITY.BUTTON_PROFILE')}
              </ActionButton>
            </div>

            <div className={classes.policies}>
              <h4>{t('MUNICIPALITY.POLICY_DOCUMENTS')}</h4>
              <ul>
                {policyDocuments.map((doc, indx) => (
                  <li key={`${indx}-${doc.id}`}>
                    <a
                      className={classes.textLink}
                      target="_blank"
                      rel="noreferrer"
                      alt={doc.title}
                      href={doc.url}
                    >
                      {doc.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={classes.policies}>
              <h4>{t('MUNICIPALITY.KEY_DOCUMENTS')}</h4>
              <ul>
                {keyDocuments.map((doc, indx) => (
                  <li key={`${indx}-${doc.id}`}>
                    <a
                      className={classes.textLink}
                      target="_blank"
                      rel="noreferrer"
                      alt={doc.title}
                      href={doc.url}
                    >
                      {doc.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {isProfileView && (
          <div data-aos="fade-left">
            <div className={classes.characteristics}>
              <table>
                <tbody>
                  <tr>
                    <td> {t('MUNICIPALITY.AREA')} </td>
                    <td>{data.area}</td>
                  </tr>

                  <tr>
                    <td> {t('MUNICIPALITY.POPULATION')} </td>
                    <td>{data.population}</td>
                  </tr>

                  <tr>
                    <td> {t('MUNICIPALITY.INHABITANTS')} </td>
                    <td>{data.inhabitants}</td>
                  </tr>
                  <tr>
                    <td> {t('MUNICIPALITY.UNITS')} </td>
                    <td className={classes.characteristicsUnitsTd}>
                      <Tooltip title={data.units}>
                        <Typography className={classes.characteristicsUnits}>
                          {unitsText}
                        </Typography>
                      </Tooltip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Collapse in={isShowMore} timeout="auto" collapsedHeight={100}>
              <div
                className={classes.description}
                dangerouslySetInnerHTML={{
                  __html: data.description,
                }}
              ></div>
            </Collapse>

            <div className={classes.actionContainer}>
              <ActionButton
                style={{ marginRight: 20 }}
                variant="outlined"
                size="small"
                onClick={e => setIsProfileView(false)}
              >
                {t('MUNICIPALITY.BUTTON_GO_BACK')}
              </ActionButton>
              <ActionButton
                variant="contained"
                color="primary"
                size="small"
                onClick={e => handleToggleShowMore()}
              >
                {t('MUNICIPALITY.BUTTON_SHOW_MORE')}
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MapsPreview.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Maps informations
   */
  data: PropTypes.object.isRequired,
};

export default MapsPreview;
